/**
 * Mapa nas regiões do AppShell (§4.3, §9.1; FE §5, DE-06, DE-C20). Fica fora do App.tsx para que a composição do mapa
 * (Fase 5) e a do herói (Fase 4, app/heroSlot.tsx) não disputem as mesmas linhas.
 *
 * Dois pontos de entrada (DE-06):
 * - `MapTopSlot` (região `mapTop`): a `MapTopBar` (migalhas, modos, seletor, legenda) é estática, no chunk `index`, e
 *   aparece antes do mapa (a legenda espera o 1º desenho, §9.6 "Carregando geometria").
 * - `MapSlot` (região `map`) e `ZoomSlot` (região `zoom`): `ElectionMap` e `ZoomControls` vêm do chunk `map`
 *   (`import('../features/map')`), pedido na etapa 2 de §15.7.4: depois do 1º render do herói, dois
 *   `requestAnimationFrame`. O herói desenha o 1º número no mesmo render em que a vista (`view`) deixa de ser null;
 *   sem vista (feed fora do ar), o mapa sai mesmo assim depois de `MAP_IMPORT_FALLBACK_MS`, sem dados (§9.5).
 *   Falha do chunk vira erro de render: a boundary `map` (§12.8, 2 s) remonta e tenta de novo.
 *
 * Retângulo (§9.8.1): no desktop e na TV, o mapa cobre o palco e recebe o retângulo de §4.3 (as mesmas funções do
 * AppShell, `shellColumns` + `mapRect`) com o `padR` das chips; no tablet e no celular o `ElectionMap` mede a caixa.
 */
import './mapSlot.css';
import { effect, signal } from '@preact/signals';
import { useEffect, useState } from 'preact/hooks';
import { t } from '../copy/strings';
import { loadGeo } from '../data/loadGeo';
import { loadMunMesh } from '../data/munMesh';
import type { ElectionMapProps } from '../features/map';
import { MapTopBar } from '../features/map/MapTopBar';
import type { LayoutMode } from '../model/types';
import { wantMunicipal } from '../state/actions';
import { mapMode } from '../state/derived';
import { useConfig, useHasFocus, useLayout } from '../state/hooks';
import { route, statics, view } from '../state/store';
import { shellColumns } from './AppShell';
import { afterContentPaint } from './firstPaint';
import { LAYOUT, mapRect } from './layoutMode';
import { municipalFocus, municipalNational, munMesh } from './munMeshState';
import { chunkFailed } from './staleBuild';

type MapModule = typeof import('../features/map');

/** Sem 1º número até este tempo (feed fora do ar), o chunk do mapa sai mesmo assim: a meta do 1º número (§15.7.1). */
export const MAP_IMPORT_FALLBACK_MS = 2500;

/** "Carregando o mapa…" depois de 600 ms sem o chunk (§9.1); o `ElectionMap` continua a mesma conta (`loadingSince`). */
export const MAP_LOADING_TEXT_MS = 600;

/** Módulo do chunk `map` carregado (lido por `MapSlot` e `ZoomSlot`). */
export const mapModule = signal<MapModule | null>(null);

let pending: Promise<MapModule> | null = null;

/**
 * Espera antes da 2ª tentativa do pedaço do mapa (ORD-F-062): numa navegação para outra página, o navegador cancela os
 * pedidos em voo e o `import()` falha ("Unable to preload CSS"); a página antiga some antes da 2ª tentativa, e nada vai
 * ao console. Numa falha de verdade, a 2ª também falha e a boundary entra 1 s depois (AC-16.5-01 no WebKit).
 */
export const MAP_RETRY_MS = 1000;

/** `import()` do chunk `map`, uma vez (uma falha libera a nova tentativa); publica o módulo em `mapModule`. */
export function loadMapChunk(): Promise<MapModule> {
  if (!pending) {
    const p = import('../features/map');
    pending = p;
    p.catch(() => {
      if (pending === p) pending = null;
      // o pedaço pode ter sumido num deploy novo ou numa volta (pré-mortem, ORD-F-146)
      chunkFailed();
    });
  }
  return pending.then((m) => {
    if (mapModule.peek() !== m) mapModule.value = m;
    return m;
  });
}

/**
 * Altura do selo de proveniência do replay no canto do mapa (ReplaySourceStamp `map`: a linha, as duas partes do rótulo
 * e "Método e arquivos"). No site do replay o enquadramento termina acima dele, para os créditos não cobrirem o mapa nem
 * as chips (vitrine do replay, ORD-F-014 item 2 b); o site ao vivo, com o selo de uma linha, não muda.
 */
export const REPLAY_STAMP_RESERVE = 120;

/**
 * Altura real (px) do selo do replay no canto do mapa, medida pelo próprio selo (`ReplaySourceStamp`, variante `map`);
 * 0 sem selo. O selo cresce além dos 120 px: quebra em mais linhas na faixa estreita (1024 e 1280), ganha a nota do
 * quadro fora da linha do tempo no resultado final e, no toque, "Método e arquivos" com 44 px. Com a reserva fixa, o
 * selo cobria o marcador de SC e o chip do Exterior (matriz do replay, 1024×768 toque).
 */
export const replayStampHeight = signal(0);

/** O selo fica a 6 px de y1 (AppShell.css); o mapa termina 8 px acima dele. */
const STAMP_BASE = 6;
const STAMP_GAP = 8;

/** Quanto o enquadramento do replay reserva sob o mapa: os 120 px, ou o selo inteiro quando ele é mais alto. */
export function replayStampReserve(stampH: number): number {
  return Math.max(REPLAY_STAMP_RESERVE, Math.ceil(stampH) + STAMP_BASE + STAMP_GAP);
}

/**
 * Retângulo do mapa em px lógicos do palco (§4.3, §9.8.1) no desktop e na TV; null nos layouts de caixa. No replay, lê
 * `replayStampHeight` (o slot que chama redesenha quando o selo muda de altura).
 */
export function stageMapRect(
  mode: LayoutMode,
  stageW: number,
  stageH: number,
  hasFocus: boolean,
  replay: boolean = import.meta.env.VITE_SURFACE === 'replay',
  stampH: number = replay ? replayStampHeight.value : 0,
): ElectionMapProps['rect'] {
  if (mode !== 'desktop' && mode !== 'tv') return null;
  const tv = mode === 'tv';
  const { x0, x1, y0, y1 } = mapRect(stageW, stageH, shellColumns(mode, stageW, hasFocus, false), tv);
  const bottom = replay && !tv ? y1 - replayStampReserve(stampH) : y1;
  return { x0, x1, y0, y1: bottom, padR: tv ? LAYOUT.TV_CHIP_RESERVE : LAYOUT.CHIP_RESERVE };
}

/**
 * Espera a etapa 2 de §15.7.4 (vista com números, ou o tempo de reserva) e dois quadros, e pede o chunk. Devolve o
 * módulo quando chega; uma falha do `import()` é lançada no render (boundary `map`).
 */
function useMapChunk(): MapModule | null {
  const mod = mapModule.value;
  const [failure, setFailure] = useState<{ err: unknown } | null>(null);
  if (failure) throw failure.err;
  useEffect(() => {
    if (mapModule.peek()) return;
    const since = performance.now();
    let alive = true;
    let started = false;
    let raf = 0;
    let offOnline: (() => void) | null = null;
    let retried = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const start = () => {
      if (started || !alive) return;
      started = true;
      raf = requestAnimationFrame(() => {
        raf = requestAnimationFrame(() => {
          raf = 0;
          // depois do registro da pintura do app, para o mapa não entrar no LCP (DECISOES §40)
          void afterContentPaint(since).then(() => {
            if (!alive) return;
            loadMapChunk().catch((err: unknown) => {
              if (!alive) return;
              // Sem rede (§10.9), a falha do chunk é o estado previsto, não erro: o slot segue em "Carregando o mapa" e
              // tenta de novo quando a rede volta (no lugar da boundary repetindo a cada 4 s sem rede; e2e AC-10.6-04).
              if (typeof navigator !== 'undefined' && navigator.onLine === false) {
                console.warn('[app] map: chunk sem rede; nova tentativa quando a rede voltar');
                const onOnline = () => {
                  offOnline?.();
                  offOnline = null;
                  started = false;
                  start();
                };
                addEventListener('online', onOnline);
                offOnline = () => removeEventListener('online', onOnline);
                return;
              }
              if (!retried) {
                retried = true;
                retryTimer = setTimeout(() => {
                  started = false;
                  start();
                }, MAP_RETRY_MS);
                return;
              }
              setFailure({ err });
            });
          });
        });
      });
    };
    const timer = setTimeout(start, MAP_IMPORT_FALLBACK_MS);
    // O render que recebe a 1ª vista é o do 1º número do herói; o efeito roda depois dele.
    const stop = effect(() => {
      if (view.value !== null) start();
    });
    return () => {
      alive = false;
      clearTimeout(timer);
      clearTimeout(retryTimer);
      offOnline?.();
      stop();
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  return mod;
}

/** Região `mapTop` (desktop, TV e tablet; a `MapTopBar` some sozinha em `flow` e `land`). */
export function MapTopSlot() {
  const { mode, stageW, stageH } = useLayout();
  const hasFocus = useHasFocus();
  const rect = stageMapRect(mode, stageW, stageH, hasFocus);
  return <MapTopBar mapHeight={rect ? rect.y1 - rect.y0 : undefined} />;
}

/** Região `map`: o `ElectionMap` do chunk `map`, depois do herói; antes dele, o estado "Carregando o mapa" (§9.1). */
export function MapSlot() {
  const mod = useMapChunk();
  const { mode, stageW, stageH } = useLayout();
  const hasFocus = useHasFocus();
  const [since] = useState(() => performance.now());
  const [slow, setSlow] = useState(false);
  const waiting = mod === null;
  useEffect(() => {
    if (!waiting) return;
    const id = setTimeout(() => setSlow(true), Math.max(0, MAP_LOADING_TEXT_MS - (performance.now() - since)));
    return () => clearTimeout(id);
  }, [waiting]);
  const rect = stageMapRect(mode, stageW, stageH, hasFocus);
  if (!mod) {
    if (!slow) return null;
    const px = (n: number) => `${n}px`;
    const style = rect
      ? { left: px(rect.x0), top: px(rect.y0), width: px(rect.x1 - rect.x0), height: px(rect.y1 - rect.y0) }
      : undefined;
    return (
      <p
        class={`map-slot__loading${mode === 'tablet' ? ' map-slot__loading--tablet' : ''}`}
        style={style}
        data-testid="map-loading"
      >
        {t('map.loading')}
      </p>
    );
  }
  const { ElectionMap } = mod;
  return <ElectionMap rect={rect} loadingSince={since} />;
}

/**
 * Malha municipal da UF em foco no site ao vivo (Fase 10, §9.2; AC-8.2-07): a ligação do chunk do mapa
 * (`features/map/munBinding.ts`), quando ele chega, só com `features.municipal_map`. Chamada só pelo `App` do site ao
 * vivo: o replay liga a mesma camada no próprio boot (`replay/boot.ts`).
 */
export function useLiveMunicipalLayer(): void {
  const mod = useConfig().features.municipal_map ? mapModule.value : null;
  useEffect(() => {
    if (!mod) return;
    const binding = mod.bindMunicipalLayer({
      session: { wantMunicipal },
      focus: () => municipalFocus(route.value),
      // o Brasil inteiro por município (regra de 31/10): as 27 malhas sob demanda, só nos modos por município
      national: () => municipalNational(route.value, mapMode.value),
      geography: () => statics.value.geography,
      status: munMesh,
      load: loadMunMesh,
      // a malha nacional simplificada (`br-mun`, o `geoMun` do manifesto)
      loadNational: (signal) => loadGeo('br-mun', signal),
    });
    return () => binding.stop();
  }, [mod]);
}

/** Região `zoom`: os `ZoomControls` do chunk `map` (só depois de o mapa chegar). */
export function ZoomSlot() {
  const mod = mapModule.value;
  if (!mod) return null;
  const { ZoomControls } = mod;
  return <ZoomControls />;
}
